/**
 * The ladder of 8 bot personas (docs/research/08 §4).
 *
 * Rules for every line:
 *  - Russian, short, warm, sporty — also when the bot wins (a win line carries a tiny tip).
 *  - Gender-neutral TOWARDS THE CHILD: no past-tense verbs or short adjectives about the child
 *    ("ты нашёл", "ты готов"). A bot may use its own gender about itself ("я зевнула").
 *  - No placeholders, no Latin notation. Bots "speak" in text bubbles only (the single voice of
 *    the app is the mascot).
 */
import type { Persona, PersonaId } from '@gambit/shared';

/** Weakest → strongest. Age grows together with strength. */
export const PERSONA_ORDER: PersonaId[] = ['petya', 'sonya', 'grisha', 'sasha', 'vika', 'lyova', 'nika', 'dima'];

export const PERSONAS: Record<PersonaId, Persona> = {
  petya: {
    id: 'petya',
    name: 'Петя',
    age: 6,
    nominalElo: 300,
    tagline: 'Первоклашка. Только выучил ходы и отвлекается на кота.',
    style:
      'Ходит почти наугад и любит двигать крайние пешки. Часто оставляет фигуры без защиты, не замечает угроз и забывает про рокировку.',
    avatar: { bg: '#7BD389', skin: '#FFD9B8', hair: '#F2C14E', hairStyle: 'spiky' },
    lines: {
      intro: [
        'Привет! Я Петя. Я уже знаю, как ходит конь! Ну… почти.',
        'Ура, играем! Только чур, мой кот Барсик будет смотреть.',
        'Я Петя! Вчера я выучил, как ходит слон. Сейчас покажу!',
        'Давай играть! Я буду стараться изо всех сил.',
      ],
      onWin: [
        'Ой, я выиграл?! Давай ещё раз — у тебя точно получится!',
        'Вот это да, у меня получилось! Мне повезло. Проверяй, что под боем, — и победа будет твоя!',
        'Ух, какая трудная партия! Сыграем ещё? Мне очень понравилось!',
      ],
      onLose: [
        'Ух ты, как здорово! Научишь меня так же?',
        'Ого! С тобой очень интересно играть. Давай ещё разок!',
        'Ты играешь сильнее меня. Я ещё потренируюсь и вернусь!',
      ],
      onDraw: [
        'Ничья! Значит, мы оба молодцы.',
        'Никто не проиграл — здорово! Сыграем ещё?',
        'Ничья? Это как дружба, только в шахматах!',
      ],
      onGoodMoveByChild: [
        'Ой! А так можно было?',
        'Ого, какой ход!',
        'Вот это да! Я такого не заметил.',
        'Ух ты! Хитро придумано!',
      ],
    },
    recommendedFromStage: 1,
  },

  sonya: {
    id: 'sonya',
    name: 'Соня',
    age: 7,
    nominalElo: 500,
    tagline: 'Обожает своего ферзя: «Он самый сильный!»',
    style:
      'Рано выводит ферзя и хватает всё, что плохо лежит. Попадается в простые ловушки и часто забывает защитить фигуру, на которую напали.',
    avatar: { bg: '#FFD23F', skin: '#FFE0C7', hair: '#8B5A2B', hairStyle: 'ponytail', accessory: 'bow' },
    lines: {
      intro: [
        'Я Соня! Мой ферзь самый сильный, сейчас увидишь!',
        'Привет! Мой ферзик уже рвётся в бой. Береги фигуры!',
        'Я Соня. Сыграем? Мой ферзь очень любит путешествовать по доске.',
      ],
      onWin: [
        'Мой ферзик всех победил! Но игра была классная. Следи за ним в следующий раз!',
        'Ура, победа! Подсказка от меня: когда мой ферзь выходит рано, на него можно нападать с пользой.',
        'Получилось! Ты здорово сражаешься. Давай ещё партию?',
      ],
      onLose: [
        'Ой! Мой ферзик говорит: ты играешь здорово!',
        'Эх, я опять вывела ферзя слишком рано. Отличная игра!',
        'Победа твоя! Ты играешь внимательнее меня. Поздравляю!',
      ],
      onDraw: [
        'Ничья! Мой ферзик говорит: было весело.',
        'Поровну! Хорошая партия, сыграем ещё?',
        'Ничья — тоже красиво. Никто никого не обидел!',
      ],
      onGoodMoveByChild: [
        'Ой-ой, мой ферзик в опасности!',
        'Ого, этого я не ждала!',
        'Какой сильный ход! Надо подумать…',
        'Ничего себе! Хороший удар.',
      ],
    },
    recommendedFromStage: 1,
  },

  grisha: {
    id: 'grisha',
    name: 'Гриша',
    age: 8,
    nominalElo: 700,
    tagline: 'Хитрюга. Выучил детский мат и ставит его всем во дворе.',
    style:
      'Каждую партию целится ферзём и слоном в слабую клеточку возле короля. Если атака отбита — расстраивается и играет небрежно. Рокировку делает редко, но угрозы в один ход видит.',
    avatar: { bg: '#FF9F45', skin: '#FFD2B0', hair: '#E8702A', hairStyle: 'curly' },
    lines: {
      intro: [
        'Я Гриша. Спорим, поставлю мат в четыре хода?',
        'Привет! У меня есть секретная ловушка. Попробуй её разгадать!',
        'Я Гриша, гроза нашего двора. Следи за своим королём!',
      ],
      onWin: [
        'Ха, сработало! Запомни эту ловушку — и больше не попадёшься.',
        'Победа моя! Секрет простой: перед ходом смотри, куда целятся мой ферзь и мой слон.',
        'Есть! Но ты держишься всё лучше. Давай реванш?',
      ],
      onLose: [
        'Эх, а я так надеялся на свои ловушки… Ты уже умеешь защищаться!',
        'Ну вот, моя ловушка раскрыта! Честная победа, поздравляю.',
        'Ладно-ладно, сегодня твой день. Сильная игра!',
      ],
      onDraw: [
        'Ничья? Хм, в следующий раз придумаю ловушку похитрее!',
        'Поровну. С тобой непросто, и это здорово!',
        'Ничья! Жму руку.',
      ],
      onGoodMoveByChild: [
        'Эй, так нечестно… то есть честно! Хороший ход.',
        'Ого, мою хитрость разгадали!',
        'Хм! Этого я не ожидал.',
        'Вот это защита!',
      ],
    },
    recommendedFromStage: 2,
  },

  sasha: {
    id: 'sasha',
    name: 'Саша',
    age: 9,
    nominalElo: 900,
    tagline: 'Ходит в шахматный кружок и играет «по правилам».',
    style:
      'Занимает центр, развивает фигуры, делает рокировку. В один ход фигуры не отдаёт, но пропускает тактику в два хода — вилки и связки. Слишком охотно меняется фигурами.',
    avatar: { bg: '#4DB5FF', skin: '#F3C9A5', hair: '#5B4636', hairStyle: 'cap', accessory: 'cap' },
    lines: {
      intro: [
        'Привет, я Саша, хожу в шахматный кружок. Сыграем по-честному?',
        'Я Саша. Тренер говорит: центр, развитие, рокировка. Проверим?',
        'Привет! Люблю спокойную игру по правилам. Начинаем?',
      ],
      onWin: [
        'Хорошая партия! Мой совет из кружка: следи за вилками — я их люблю.',
        'Спасибо за игру! Совет из кружка: перед ходом ищи шахи, взятия и угрозы.',
        'Победа моя, но бороться было трудно. Жму руку!',
      ],
      onLose: [
        'Сильно сыграно! Жму руку.',
        'Поздравляю! Расскажу тренеру, какая была партия.',
        'Честная победа. Кажется, я пропустил тактику. Буду тренироваться!',
      ],
      onDraw: [
        'Ничья. Честный результат!',
        'Поровну! Хорошая борьба, спасибо.',
        'Ничья — значит, мы оба играли крепко.',
      ],
      onGoodMoveByChild: [
        'Хороший ход! Так и в кружке учат.',
        'Ого, это сильно.',
        'Точно сыграно!',
        'Ух, я этого не увидел.',
      ],
    },
    recommendedFromStage: 3,
  },

  vika: {
    id: 'vika',
    name: 'Вика',
    age: 10,
    nominalElo: 1100,
    tagline: 'Тактик. Решает задачки на скорость.',
    style:
      'Любит вилки, связки и вскрытые нападения, ставит ловушки. Нетерпелива: атакует слишком рано и оставляет своего короля без защиты. В эндшпиле играет слабее.',
    avatar: { bg: '#9B7BFF', skin: '#FFE0C7', hair: '#2B2B3A', hairStyle: 'bob' },
    lines: {
      intro: [
        'Я Вика. Обожаю вилки и связки. Следи за своими фигурами!',
        'Привет! Сегодня я уже решила двадцать задачек. Проверим, кто зорче?',
        'Я Вика. Предупреждаю: люблю ловушки! Играем?',
      ],
      onWin: [
        'Вот и победа! Реши десять задачек — и приходи за реваншем.',
        'Моя тактика сработала! Совет: перед каждым ходом смотри, какие фигуры стоят без защиты.',
        'Победа! Но мой король тоже дрожал. Было интересно!',
      ],
      onLose: [
        'Вот это игра! Ты видишь дальше меня.',
        'Ой, я слишком спешила с атакой. Заслуженная победа!',
        'Красиво! Такую тактику я запишу себе в тетрадку.',
      ],
      onDraw: [
        'Ничья! Ни одна ловушка не сработала. Уважаю!',
        'Поровну. С тобой нужно считать внимательно!',
        'Ничья — хороший результат для обеих сторон.',
      ],
      onGoodMoveByChild: [
        'Ого, тактика! Красиво.',
        'Так, мою ловушку заметили…',
        'Сильный удар! Надо считать.',
        'Вот это зоркий глаз!',
      ],
    },
    recommendedFromStage: 4,
  },

  lyova: {
    id: 'lyova',
    name: 'Лёва',
    age: 12,
    nominalElo: 1400,
    tagline: 'Спокойный «профессор» в очках. Второй разряд.',
    style:
      'Играет надёжно и без спешки, знает пару дебютов и хорошо разыгрывает простые эндшпили. Почти не ошибается в один ход, но бывает пассивным — его можно переиграть активной игрой.',
    avatar: { bg: '#2BB3A3', skin: '#F3C9A5', hair: '#A9825A', hairStyle: 'short', accessory: 'glasses' },
    lines: {
      intro: [
        'Здравствуй. Я Лёва, у меня второй разряд. Я никуда не тороплюсь.',
        'Привет. Предлагаю сыграть спокойно и вдумчиво.',
        'Я Лёва. Люблю крепкие позиции и эндшпили. Начнём?',
      ],
      onWin: [
        'Спасибо за партию. Давай потом посмотрим, где можно было сыграть точнее.',
        'Спасибо за партию. Совет: играй активнее, я не люблю, когда на меня нападают.',
        'Победа моя, но борьба была достойная. Буду рад сыграть ещё.',
      ],
      onLose: [
        'Достойная победа! Твой план оказался лучше моего.',
        'Поздравляю. Активная игра — это было правильно.',
        'Хм, я сыграл слишком пассивно. Отличная партия!',
      ],
      onDraw: [
        'Ничья. Логичный итог крепкой игры.',
        'Поровну. Позиция была равна — всё честно.',
        'Ничья. Спасибо, было над чем подумать.',
      ],
      onGoodMoveByChild: [
        'Хороший ход. Уважаю.',
        'Точно сыграно.',
        'Интересная идея! Надо подумать.',
        'Сильное решение.',
      ],
    },
    recommendedFromStage: 5,
  },

  nika: {
    id: 'nika',
    name: 'Ника',
    age: 15,
    nominalElo: 1800,
    tagline: 'Первый разряд, атакующий стиль, всегда в наушниках.',
    style:
      'Играет гамбиты, жертвует фигуры ради атаки и сурово наказывает за отставание в развитии. Иногда увлекается и жертвует неправильно — тогда нужно спокойно защищаться.',
    avatar: { bg: '#E5484D', skin: '#FFD9B8', hair: '#B33A5B', hairStyle: 'long', accessory: 'headphones' },
    lines: {
      intro: [
        'Я Ника, первый разряд. Люблю атаковать. Береги короля!',
        'Привет. Сразу предупреждаю: играю остро. Поехали?',
        'Я Ника. Музыка в наушниках, фигуры — в атаку. Начинаем!',
      ],
      onWin: [
        'Победа моя! Против атаки трудно всем — попробуй рокироваться пораньше.',
        'Моя взяла. Совет: сначала разбуди все фигуры, и только потом иди в бой.',
        'Хорошая борьба! С каждым разом атаковать тебя всё труднее.',
      ],
      onLose: [
        'Ого. Моя атака отбита — такое удаётся немногим!',
        'Я пожертвовала лишнего, и меня наказали. Сильная игра!',
        'Респект! Спокойная защита — это мощно.',
      ],
      onDraw: [
        'Ничья. Моя атака упёрлась в стену — уважаю.',
        'Поровну! Острая получилась партия.',
        'Ничья. Неплохо, совсем неплохо.',
      ],
      onGoodMoveByChild: [
        'Хороший ход. Становится интересно!',
        'Ого, контрудар!',
        'Так, придётся считать внимательнее.',
        'Сильно. Мне нравится, как ты играешь.',
      ],
    },
    recommendedFromStage: 6,
  },

  dima: {
    id: 'dima',
    name: 'Дима',
    age: 17,
    nominalElo: 2500,
    tagline: 'Юный гроссмейстер. Говорит мало, играет очень сильно.',
    style:
      'Играет в полную силу и без поблажек. Успех против Димы — это не только победа, а каждый лишний ход без грубой ошибки.',
    avatar: { bg: '#26304F', skin: '#EFC7A4', hair: '#1C1C28', hairStyle: 'short', accessory: 'scarf' },
    lines: {
      intro: [
        'Дима. Гроссмейстер. Поблажек не будет.',
        'Привет. Играю в полную силу — так честнее.',
        'Садись. Покажи всё, что умеешь.',
      ],
      onWin: [
        'Партия окончена. Ты держишься всё дольше. Расти дальше.',
        'Хорошая попытка. Посмотри с тренером один трудный момент — и возвращайся.',
        'Спасибо за игру. Проигрывать сильному не стыдно, это тоже тренировка.',
      ],
      onLose: [
        'Поздравляю. Это было по-настоящему сильно. Запомни этот день.',
        'Сдаюсь. Великолепная партия.',
        'Вот это уровень. Жму руку, коллега.',
      ],
      onDraw: [
        'Ничья с гроссмейстером. Это серьёзный результат.',
        'Ничья. Достойно.',
        'Поровну. Ты умеешь держать удар.',
      ],
      onGoodMoveByChild: [
        'Сильный ход.',
        'Точно.',
        'Уважаю.',
        'Хорошо. Очень хорошо.',
      ],
    },
    recommendedFromStage: 8,
  },
};

/** Personas in ladder order (weakest first). */
export function listPersonas(): Persona[] {
  return PERSONA_ORDER.map((id) => PERSONAS[id]);
}

/** Lookup that tolerates unknown ids (e.g. from stored records of an older version). */
export function getPersona(id: string): Persona | undefined {
  return Object.hasOwn(PERSONAS, id) ? PERSONAS[id as PersonaId] : undefined;
}
